import * as api from '@owl-nest/api-client/latest'
import * as dateUtils from '@owl-nest/date'
import * as Enum from '@owl-nest/legacy-enum'

import * as orderItem from './orderItem'

export function isPaidByCheck(order: api.Order): boolean {
  return order.payment_method === 'check'
}

export function isProcessing(order: api.Order): boolean {
  if (order.status === 'awaiting-confirmation' && isPaidByCheck(order)) {
    return false
  }

  return ['processing', 'selecting-payment', 'awaiting-confirmation'].indexOf(order.status) > -1
}

export function isFailed(order: api.Order): boolean {
  return ['aborted', 'payment-invalid', 'payment-reimbursed', 'payment-refunded', 'error'].indexOf(order.status) > -1
}

export function isCancelled(order: api.Order): boolean {
  return order.status === 'cancelled'
}

export function isSucceeded(order: api.Order): boolean {
  if (order.status === 'awaiting-confirmation' && isPaidByCheck(order)) {
    return true
  }

  return ['payment-completed', 'shipped', 'payment-done'].indexOf(order.status) > -1
}

export const OrderStatus = Enum.create({
  AWAITING_PAYMENT: 0,
  AWAITING_DELIVERY: 1,
  AWAITING_BANKWIRE: 2,
  REIMBURSMENT_FAILURE: 3,
  PAYMENT_FAILURE: 4,
  PAYMENT_REIMBURSED: 5,
  PAYMENT_CANCELED: 6,
  PAYMENT_INVALID: 7,
  SUBSCRIPTION_CANCELED: 8,
})
export type OrderStatus = Enum.ValueOf<typeof OrderStatus>

export function getOrderStatus<WITH_PROJECT = false, EXTRA_FIELDS extends string = ''>(
  order: api.Order<WITH_PROJECT, EXTRA_FIELDS>,
  project: api.Project,
  user?: api.AuthenticatedUser,
): OrderStatus | undefined {
  if (order.payment_method === api.PaymentMethod.BANKWIRE && order.status === 'awaiting-confirmation') {
    return OrderStatus.AWAITING_BANKWIRE
  }
  if (user?.is_staff && order.status === 'awaiting-confirmation') {
    return OrderStatus.AWAITING_PAYMENT
  }
  if (/** TODO when the api is capable to send the information */ false) {
    return OrderStatus.REIMBURSMENT_FAILURE
  }
  if (order.status === 'payment-reimbursed') {
    return OrderStatus.PAYMENT_REIMBURSED
  }
  if (order.status === 'cancelled') {
    return OrderStatus.PAYMENT_CANCELED
  }
  if (order.status === 'payment-invalid') {
    return OrderStatus.PAYMENT_INVALID
  }
  if (order.status === 'error') {
    return OrderStatus.PAYMENT_FAILURE
  }
  if (order.subscription?.status === api.SubscriptionStatus.CANCELED) {
    return OrderStatus.SUBSCRIPTION_CANCELED
  }
  const lastDelivery = getLastDelivery(order, project)
  if (
    lastDelivery &&
    dateUtils.addMonths(lastDelivery, 2) < new Date() &&
    order.shipping_status !== api.ShippingStatus.DELIVERED
  ) {
    return OrderStatus.AWAITING_DELIVERY
  }
}

export function getLastDelivery<WITH_PROJECT = false, EXTRA_FIELDS extends string = ''>(
  order: api.Order<WITH_PROJECT, EXTRA_FIELDS>,
  project: api.Project,
): Date | undefined {
  const safeOrders = order.items ?? []

  if (safeOrders.length === 0) {
    return undefined
  }

  const lastDeliveryDate = safeOrders
    .map((item) => {
      const deliveryDate = orderItem.getReward(item, project)?.shippings?.date_delivery
      if (!deliveryDate) {
        return undefined
      }
      return dateUtils.parse(deliveryDate, 'yyyy-MM', new Date())
    })
    .filter((date): date is Date => date !== undefined)
    .sort((a, b) => b.getTime() - a.getTime())[0]

  return lastDeliveryDate
}
